// Paired-daemon storage. Each entry keeps its own phone keypair so the relay
// can't correlate one phone across daemons.
import { b64encode, generateKeypair } from "./crypto.js";

export interface DaemonEntry {
  daemonId: string;
  portal: string; // portal origin, e.g. https://relay.example.com
  daemonPub: string; // daemon static public key, base64
  myPriv: string; // this phone's static private key for this daemon, base64
  name: string; // project label from the QR
}

const KEY = "fabrika.daemons";
// Which daemon to reopen on a fresh launch — so the app lands back in the
// project you were last using instead of an arbitrary one.
const LAST_KEY = "fabrika.lastDaemon";

export function listDaemons(): DaemonEntry[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "[]") as DaemonEntry[];
  } catch {
    return [];
  }
}

export function getDaemon(daemonId: string): DaemonEntry | undefined {
  return listDaemons().find((d) => d.daemonId === daemonId);
}

export function saveDaemon(entry: DaemonEntry): void {
  const all = listDaemons().filter((d) => d.daemonId !== entry.daemonId);
  all.push(entry);
  localStorage.setItem(KEY, JSON.stringify(all));
}

export function removeDaemon(daemonId: string): void {
  localStorage.setItem(KEY, JSON.stringify(listDaemons().filter((d) => d.daemonId !== daemonId)));
  if (localStorage.getItem(LAST_KEY) === daemonId) localStorage.removeItem(LAST_KEY);
}

// getLastDaemonId returns the last-opened daemon if it's still paired, else the
// first paired daemon, else null (nothing paired yet).
export function getLastDaemonId(): string | null {
  const all = listDaemons();
  if (!all.length) return null;
  const last = localStorage.getItem(LAST_KEY);
  if (last && all.some((d) => d.daemonId === last)) return last;
  return all[0].daemonId;
}

export function setLastDaemonId(daemonId: string): void {
  localStorage.setItem(LAST_KEY, daemonId);
}

// newPhoneKey mints the per-daemon static key at pairing time.
export function newPhoneKey(): string {
  return b64encode(generateKeypair().priv);
}
